// "Send to" for named people or managers: a control that says what is picked, opening the
// searchable tick list. Drawn over the page, so the form's own scrolling cannot cut it off.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search } from 'lucide-react';
import { InlineLoader } from '../loading';
import type { PickOption } from './bannerOptions';

/**
 * Searchable multi-select. Local on purpose — the app has no shared employee picker
 * with codes and team sizes, and lifting one out is a separate refactor.
 */
function MultiPicker({
  options,
  selected,
  onChange,
  loading,
  placeholder,
  emptyHint,
}: {
  options: PickOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  loading: boolean;
  placeholder: string;
  emptyHint: string;
}) {
  const [q, setQ] = useState('');
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(
      (o) =>
        o.code.toLowerCase().includes(needle) || o.name.toLowerCase().includes(needle)
    );
  }, [options, q]);

  const toggle = (code: string) => {
    if (selectedSet.has(code)) onChange(selected.filter((c) => c !== code));
    else onChange([...selected, code]);
  };

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((o) => selectedSet.has(o.code));

  return (
    <div className="rounded-lg border border-gray-300 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 bg-gray-50">
        <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={placeholder}
          autoFocus
          className="min-w-0 flex-1 bg-transparent text-sm focus:outline-none"
        />
        {/* Acts on the FILTERED set, not the whole list — after typing a search
            the visible rows are what "all" means, and selecting 500 hidden
            people because the box happened to be focused would be a nasty
            surprise. Unions with anything already picked outside the filter. */}
        {filtered.length > 0 && (
          <button
            type="button"
            onClick={() => {
              if (allFilteredSelected) {
                const drop = new Set(filtered.map((o) => o.code));
                onChange(selected.filter((c) => !drop.has(c)));
              } else {
                onChange([...new Set([...selected, ...filtered.map((o) => o.code)])]);
              }
            }}
            className="text-xs font-medium text-primary-600 hover:text-primary-700 flex-shrink-0"
          >
            {allFilteredSelected
              ? `Deselect ${q.trim() ? 'these' : 'all'} (${filtered.length})`
              : `Select ${q.trim() ? 'these' : 'all'} (${filtered.length})`}
          </button>
        )}
        {selected.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-xs font-medium text-gray-500 hover:text-gray-700 flex-shrink-0"
          >
            Clear ({selected.length})
          </button>
        )}
      </div>

      <div className="max-h-56 overflow-y-auto divide-y divide-gray-50">
        {loading ? (
          <div className="px-3 py-6 flex items-center justify-center gap-2 text-sm text-gray-400">
            <InlineLoader label="Loading…" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-gray-400">
            {options.length === 0 ? emptyHint : 'No matches'}
          </div>
        ) : (
          filtered.slice(0, 200).map((o) => (
            <label
              key={o.code}
              className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50"
            >
              <input
                type="checkbox"
                checked={selectedSet.has(o.code)}
                onChange={() => toggle(o.code)}
                className="w-4 h-4 rounded border-gray-300 accent-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-900 truncate flex-1">{o.name}</span>
              {o.teamSize != null && (
                <span className="text-xs text-gray-400 flex-shrink-0">
                  {o.teamSize} in team
                </span>
              )}
              <span className="text-xs text-gray-400 font-mono flex-shrink-0">{o.code}</span>
            </label>
          ))
        )}
        {!loading && filtered.length > 200 && (
          <div className="px-3 py-2 text-xs text-gray-400 text-center">
            Showing first 200 of {filtered.length} — keep typing to narrow.
          </div>
        )}
      </div>
    </div>
  );
}

/** About how tall the open list stands: the search row, the rows and the "first 200" line. */
const PANEL_HEIGHT = 300;
const PANEL_MIN_WIDTH = 320;

export function PeoplePicker({
  options,
  selected,
  onChange,
  loading,
  emptyText,
  placeholder,
  emptyHint,
}: {
  options: PickOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  loading: boolean;
  /** What the control says with nobody picked. */
  emptyText: string;
  placeholder: string;
  emptyHint: string;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; width: number; top?: number; bottom?: number } | null>(null);

  // As MultiSearchableSelect: up to two picks by name; past that, a count reads better.
  const summary = useMemo(() => {
    if (selected.length === 0) return '';
    if (selected.length > 2) return `${selected.length.toLocaleString()} selected`;
    return selected.map((code) => options.find((o) => o.code === code)?.name ?? code).join(', ');
  }, [selected, options]);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (!r) return;
      const below = window.innerHeight - r.bottom;
      const openUp = below < PANEL_HEIGHT && r.top > below;
      const width = Math.min(Math.max(r.width, PANEL_MIN_WIDTH), window.innerWidth - 16);
      setPos({
        left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)),
        width,
        ...(openUp ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
      });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
      >
        <span className={`truncate text-left ${summary ? 'text-gray-900' : 'text-gray-500'}`}>
          {summary || emptyText}
        </span>
        <ChevronDown className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && pos && createPortal(
        <div
          ref={popRef}
          // 140 is the ladder's top step for a panel drawn on <body> (see SearchableSelect).
          style={{ position: 'fixed', left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, zIndex: 140 }}
          className="rounded-lg bg-white shadow-lg"
        >
          <MultiPicker
            options={options}
            selected={selected}
            onChange={onChange}
            loading={loading}
            placeholder={placeholder}
            emptyHint={emptyHint}
          />
        </div>,
        document.body
      )}
    </>
  );
}
